import { useEffect, useState, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { toast } from 'react-toastify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { transformQuillHtml } from '../utils/quillHtmlTransform';

function Standalone() {
  const [pageTitle, setPageTitle] = useState(null);
  const [pageContent, setPageContent] = useState(null);

  const fetchPageContent = useCallback(async () => {
    const result = await contentService.getPageBySlug('privacy-policy');
    if (result.success && result.data) {
      setPageTitle(result.data.title);
      setPageContent(result.data.content);
    }
  }, []);


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Standalone] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    },
    onPageUpdated: (slug, title) => {
      if (slug === 'privacy-policy') {
        fetchPageContent();
        toast.info(`${title} page has been updated`);
      }
    },
    onPageDeleted: (slug) => {
      if (slug === 'privacy-policy') {
        setPageTitle(null);
        setPageContent(null);
      }
    }
  });

  useEffect(() => {
    // Handle window load event (fade out loader)
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    // If window is already loaded, trigger immediately
    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    // Initialize WOW animations
    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  useEffect(() => {
    if (featureFlags.useCmsContent()) {
      fetchPageContent();
    }
  }, [fetchPageContent]);

  return (
    <Layout>
      {/* Parallax Banner - Using banner7.jpg for standalone page */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* START PRIVACY POLICY CONTENT */}
      <div className="about_content">
        <div className="container">
          {pageContent ? (
            /* API content */
            <>
              <div className="row">
                <div className="col-12 text-center wow slideInUp" data-wow-duration="2s">
                  <h1 className="heading">{pageTitle}</h1>
                </div>
              </div>
              <div className="row mt-5">
                <div className="col-lg-10 offset-lg-1">
                  <div className="cms-content text-left" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(transformQuillHtml(pageContent), { ADD_ATTR: ['style'] }) }} />
                </div>
              </div>
            </>
          ) : (
            /* Hardcoded fallback */
            <>
              {/* Page Header */}
              <div className="row">
                <div className="col-12 text-center wow slideInUp" data-wow-duration="2s">
                  <h1 className="heading">Privacy & Cookies Policy</h1>
                  <p className="para_text mb-2">
                    At Touchline Memories, we are committed to protecting your privacy and ensuring the security of your personal information.
                    This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or
                    make a purchase from our store.
                  </p>
                  <p className="text-muted small">
                    <strong>Last Updated:</strong> December 13, 2025 | <strong>Effective Date:</strong> January 1, 2025
                  </p>
                </div>
              </div>

              {/* Privacy Policy Content */}
              <div className="row mt-5">
                <div className="col-lg-10 offset-lg-1">

                  {/* Section 1: Information We Collect */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">1. Information We Collect</h2>
                    <h4 className="mb-3">Personal Information</h4>
                    <p>We collect information that you provide directly to us, including:</p>
                    <ul className="mb-4">
                      <li>Name and contact information (email address, phone number, shipping address)</li>
                      <li>Payment information (processed securely through Stripe)</li>
                      <li>Account credentials (username, password)</li>
                      <li>Purchase history and preferences</li>
                      <li>Communication preferences</li>
                    </ul>

                    <h4 className="mb-3">Automatically Collected Information</h4>
                    <p>When you visit our website, we automatically collect certain information about your device, including:</p>
                    <ul className="mb-3">
                      <li>IP address and browser type</li>
                      <li>Operating system and device information</li>
                      <li>Pages visited and time spent on our site</li>
                      <li>Referring website and search terms used</li>
                    </ul>
                  </section>

                  {/* Section 2: How We Use Your Information */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">2. How We Use Your Information</h2>
                    <p>We use the information we collect to:</p>
                    <ul className="mb-3">
                      <li>Process and fulfill your orders</li>
                      <li>Communicate with you about your orders, account, and customer service inquiries</li>
                      <li>Send you marketing communications (with your consent)</li>
                      <li>Improve our website, products, and services</li>
                      <li>Detect and prevent fraud and security issues</li>
                      <li>Comply with legal obligations and enforce our terms</li>
                      <li>Analyze website usage and performance through Google Analytics</li>
                    </ul>
                  </section>

                  {/* Section 3: Cookies and Tracking Technologies */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">3. Cookies and Tracking Technologies</h2>
                    <p className="mb-3">
                      We use cookies and similar tracking technologies to enhance your browsing experience, analyze website traffic,
                      and understand where our visitors are coming from.
                    </p>

                    <h4 className="mb-3">Types of Cookies We Use:</h4>
                    <ul className="mb-4">
                      <li><strong>Essential Cookies:</strong> Required for the website to function properly (shopping cart, checkout)</li>
                      <li><strong>Analytics Cookies:</strong> Help us understand how visitors interact with our website (Google Analytics)</li>
                      <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
                      <li><strong>Marketing Cookies:</strong> Used to deliver personalized advertisements (with your consent)</li>
                    </ul>

                    <p className="mb-3">
                      You can control cookies through your browser settings. However, disabling certain cookies may limit your ability
                      to use some features of our website. We respect your cookie preferences and will only use analytics and marketing
                      cookies with your explicit consent.
                    </p>
                  </section>

                  {/* Section 4: How We Share Your Information */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">4. How We Share Your Information</h2>
                    <p>We do not sell your personal information. We may share your information with:</p>
                    <ul className="mb-3">
                      <li><strong>Service Providers:</strong> Third parties who assist us in operating our website and processing orders (payment processors, shipping carriers)</li>
                      <li><strong>Analytics Partners:</strong> Google Analytics to help us understand website usage</li>
                      <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                      <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
                    </ul>
                  </section>

                  {/* Section 5: Your Rights Under GDPR */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">5. Your Rights Under GDPR</h2>
                    <p className="mb-3">If you are located in the European Union, you have the following rights regarding your personal data:</p>
                    <ul className="mb-4">
                      <li><strong>Right to Access:</strong> You can request a copy of the personal data we hold about you</li>
                      <li><strong>Right to Rectification:</strong> You can request corrections to inaccurate or incomplete data</li>
                      <li><strong>Right to Erasure:</strong> You can request deletion of your personal data ("right to be forgotten")</li>
                      <li><strong>Right to Restrict Processing:</strong> You can request that we limit how we use your data</li>
                      <li><strong>Right to Data Portability:</strong> You can request your data in a portable format</li>
                      <li><strong>Right to Object:</strong> You can object to certain data processing activities</li>
                      <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent for marketing communications at any time</li>
                    </ul>
                    <p>To exercise these rights, please contact us at <a href="mailto:privacy@touchlinememories.com">privacy@touchlinememories.com</a></p>
                  </section>

                  {/* Section 6: Data Security */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">6. Data Security</h2>
                    <p className="mb-3">We implement industry-standard security measures to protect your personal information, including:</p>
                    <ul className="mb-4">
                      <li>SSL/TLS encryption for all data transmission</li>
                      <li>Secure payment processing through PCI-compliant payment providers (Stripe)</li>
                      <li>Regular security audits and vulnerability assessments</li>
                      <li>Access controls and authentication measures for our systems</li>
                      <li>Employee training on data protection and privacy</li>
                    </ul>
                    <p className="mb-3">
                      However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to
                      protect your personal information, we cannot guarantee absolute security.
                    </p>
                  </section>

                  {/* Section 7: Data Retention */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">7. Data Retention</h2>
                    <p>We retain your personal information for as long as necessary to:</p>
                    <ul className="mb-3">
                      <li>Fulfill the purposes outlined in this Privacy Policy</li>
                      <li>Comply with legal and regulatory requirements (tax, accounting, etc.)</li>
                      <li>Resolve disputes and enforce our agreements</li>
                    </ul>
                    <p>Account information is retained until you request deletion or close your account.</p>
                  </section>

                  {/* Section 8: Third-Party Links */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">8. Third-Party Links</h2>
                    <p>
                      Our website may contain links to third-party websites. We are not responsible for the privacy practices or
                      content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit.
                    </p>
                  </section>

                  {/* Section 9: Children's Privacy */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">9. Children's Privacy</h2>
                    <p>
                      Our website is not intended for children under the age of 13. We do not knowingly collect personal information
                      from children under 13. If you believe we have collected information from a child under 13, please contact us
                      immediately so we can delete it.
                    </p>
                  </section>

                  {/* Section 10: Changes to This Policy */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">10. Changes to This Policy</h2>
                    <p>
                      We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the
                      new policy on this page and updating the "Last Updated" date. Your continued use of our website after changes
                      are posted constitutes your acceptance of the revised policy.
                    </p>
                  </section>

                  {/* Section 11: Contact Us */}
                  <section className="mb-5">
                    <h2 className="gradient-text1 mb-3">11. Contact Us</h2>
                    <p className="mb-3">If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</p>
                    <ul className="list-unstyled">
                      <li className="mb-2"><strong>Email:</strong> <a href="mailto:privacy@touchlinememories.com">privacy@touchlinememories.com</a></li>
                      <li className="mb-2"><strong>Phone:</strong> <a href="tel:+442012345678">+44 (0) 20 1234 5678</a></li>
                      <li className="mb-2"><strong>Address:</strong> Touchline Memories Ltd, 123 Privacy Street, London, UK, EC1A 1BB</li>
                    </ul>
                    <p className="mt-4">We will respond to your inquiries within 30 days.</p>
                  </section>

                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {/* END PRIVACY POLICY CONTENT */}
    </Layout>
  );
}

export default Standalone;
